import classNames from 'classnames';
import { reduce } from 'lodash';
import React from 'react';

import { Omit } from '../../declarations';

import './SideBySideStyle.less';

export interface ISideBySideBaseField {
  key: string;
  label?: React.ReactNode;
  type: 'FIELD' | 'HEADER' | 'HEADLINE_FIELD' | 'SUBHEADER';
}

export interface ISideBySideSubField extends Omit<ISideBySideBaseField, 'type'> {
  showSubFields?: boolean;
  subFields: ISideBySideField[];
  type: 'SUBFIELD';
}

export type ISideBySideField = ISideBySideSubField | ISideBySideBaseField;

export interface ISideBySideColumnData {
  cell: React.ReactNode;
}

export interface ISideBySideColumn {
  [keyName: string]: ISideBySideColumnData;
}

export interface ISideBySideProps {
  columns: ISideBySideColumn[];
  fields: ISideBySideField[];
}

interface IFieldColumnProps {
  columnType: string;
  hideOnMobile?: boolean;
}

interface IFieldRowProps {
  field: ISideBySideField;
  hideOnMobile?: boolean;
  hideOnDesktop?: boolean;
}

const FieldColumn: React.SFC<IFieldColumnProps> = props => {
  const { columnType, hideOnMobile } = props;

  switch (columnType) {
    case 'HEADER':
      return (
        <div
          className={classNames('od-side-by-side__header-col col-xs-6 col-sm-4', {
            'hidden-sm-down': hideOnMobile,
          })}
        >
          {props.children}
          {<div className="od-side-by-side__vs-badge">vs</div>}
        </div>
      );
    default:
      return (
        <div
          className={classNames(
            'od-side-by-side__col--right od-side-by-side__col col-xs-6 col-sm-4',
            { 'hidden-sm-down': hideOnMobile },
          )}
        >
          {props.children}
        </div>
      );
  }
};

const FieldRow: React.SFC<IFieldRowProps> = props => {
  const { key, label, type } = props.field;
  const { hideOnMobile, hideOnDesktop } = props;

  const renderedLabel =
    type === 'SUBHEADER' ? <div className="od-side-by-side__subheader">{label}</div> : label;

  const rowClass =
    type === 'HEADLINE_FIELD' ? 'od-side-by-side__row-headline' : 'od-side-by-side__row';

  return (
    <div
      className={classNames(`row ${rowClass}`, {
        'hidden-sm-down': hideOnMobile,
        'hidden-sm-up': hideOnDesktop,
      })}
      key={`od-side-by-side__row-${key}`}
    >
      <div
        className={classNames('od-side-by-side__col col-xs-12 col-sm-4', {
          'od-side-by-side__no-padding': !label,
        })}
      >
        {renderedLabel}
      </div>
      {props.children}
    </div>
  );
};

const getFlattenedFields = (fields: ISideBySideField[], hideOnDesktop: boolean = false) =>
  reduce(
    fields,
    (results, field: ISideBySideField) => {
      results.push([field, hideOnDesktop]);

      if (field.type === 'SUBFIELD') {
        return results.concat(getFlattenedFields(field.subFields, !field.showSubFields));
      }

      return results;
    },
    [],
  );

const getFieldRows = (fields: ISideBySideField[], columns: ISideBySideColumn[]) =>
  getFlattenedFields(fields).map(fieldData => {
    const [field, hideOnDesktop] = fieldData;
    const fieldColumns = columns.map((column: ISideBySideColumn, columnIndex: number) => {
      const { key, showSubFields, type } = field;
      const columnData = column[key];
      const columnKey = `${columnIndex}-${key}`;
      const showEmptyColumn =
        !columnData || type === 'SUBHEADER' || (type === 'SUBFIELD' && showSubFields);
      return (
        <FieldColumn columnType={field.type} key={columnKey} hideOnMobile={showEmptyColumn}>
          {showEmptyColumn ? null : columnData.cell}
        </FieldColumn>
      );
    });
    return (
      <FieldRow
        field={field}
        key={`od-side-by-side__row-${field.key}`}
        hideOnMobile={field.type === 'SUBFIELD'}
        hideOnDesktop={hideOnDesktop}
      >
        {fieldColumns}
      </FieldRow>
    );
  });

const SideBySide: React.SFC<ISideBySideProps> = (props: ISideBySideProps) => {
  const { columns, fields } = props;
  const fieldRows = getFieldRows(fields, columns);

  return <div className="od-side-by-side">{fieldRows}</div>;
};

export default SideBySide;
